import { Disclosure } from '@headlessui/react'
import classNames from 'classnames'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { ChevronToggle } from 'app/components/ChevronToggle'
import { DateComponent } from 'app/components/DateComponent'

export const HighlightActivityContext = createContext<string | undefined>(undefined)
export const CurrentActivityIdContext = createContext<string | undefined>(undefined)

type Props = {
  title?: string | React.ReactNode | null
  activityDate?: Date | string | undefined | null
  /**
   * Rendering options forwarded to `activityDate`. `timezone` is for a timestamp that belongs
   * to a place rather than to the viewer, e.g. an appointment at a location in another zone;
   * `dateOnly` states whether it carries a time, rather than leaving that to be inferred.
   */
  activityDateOptions?: { timezone?: string | null; dateOnly?: boolean }
  /**
   * Component next to the title. This is often an icon with a function.
   */
  actions?: React.ReactNode | undefined | null
  icon?: React.ReactNode | null
  /**
   * The content of the card above the drawer. This is often empty.
   * This often loads new data that's not present in the drawer. Eg. Call Audio
   */
  content?: React.ReactNode | null
  /**
   * The content of the card when there is an error.
   */
  errorContent?: React.ReactNode | null
  /**
   * Component to show a section of the drawer when drawer is closed.
   * it is often a summary of the drawer content and it does not load new data.
   */
  preview?: React.ReactNode | null
  /**
   * The content of the drawer.
   */
  drawer?: React.ReactNode | null
  /**
   * Whether the drawer is opened by default.
   */
  alwaysOpen?: boolean
  /**
   * The content of the AI. This is often empty. Call is one example that uses this.
   */
  aiContent?: React.ReactNode | undefined | null
  tags?: React.ReactNode
  /**
   * Applies specific css classes to the first card in a list of cards.
   */
  isFirst?: boolean
  /**
   * Applies specific css classes to the last card in a list of cards.
   */
  isLast?: boolean
  /**
   * Callback function when the drawer is closed.
   * @param open boolean, drawer state
   * @returns void
   */
  /**
   * Changes icon style and removes the space below it.
   */
  singleActivity?: boolean
  /**
   * Hides chevron icon when there is no content. Eg. Patient Card.
   */
  hideChevronIcon?: boolean
  /**
   * Drop the title, date, tag rows and timeline rail, leaving only the content.
   *
   * For a card rendered under something that already states all of that, such
   * as a response queue row. It forces the disclosure open, because the header
   * is also what opens the card and a hidden toggle would leave it shut with no
   * way in.
   */
  hideHeader?: boolean
  onClick?: (open: boolean) => void
}

export function ActivityCard({
  content,
  errorContent,
  preview,
  drawer,
  alwaysOpen,
  aiContent,
  title,
  activityDate,
  activityDateOptions,
  actions,
  icon,
  tags,
  isFirst = false,
  isLast = false,
  singleActivity = false,
  hideChevronIcon = false,
  hideHeader = false,
  onClick,
}: Props) {
  const highlightActivityId = useContext(HighlightActivityContext)
  const currentActivityId = useContext(CurrentActivityIdContext)
  const [highlightActive, setHighlightActive] = useState(false)

  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (currentActivityId && highlightActivityId === currentActivityId) {
      setHighlightActive(true)
      cardRef.current?.scrollIntoView({ behavior: `smooth`, block: `center` })
      const timeoutId = setTimeout(() => setHighlightActive(false), 5000)
      return () => clearTimeout(timeoutId)
    }
  }, [currentActivityId, highlightActivityId])
  const btnRef = useRef<HTMLButtonElement>(null)

  return (
    <div ref={cardRef} className={`flex w-full gap-2 px-4 ${singleActivity ? `bg-surface-subtle` : ``}`}>
      {!singleActivity && !hideHeader && (
        <div className="relative flex shrink-0 flex-col items-center gap-y-1">
          <hr className={`border-default pt-[13px] ${isFirst ? `` : `border-l`}`} />
          <div className="shrink-0">{icon}</div>
          <hr className={`h-full border-default ${isLast ? `` : `border-l`}`} />
        </div>
      )}
      <Disclosure
        as="div"
        x-component-name="activity-record-data"
        className="flex min-w-0 grow flex-col gap-2 pb-3"
        defaultOpen={alwaysOpen || hideHeader}
      >
        {({ open }) => (
          <>
            {!hideHeader && (
              <div className="mt-4 flex h-6 items-center justify-between">
                <Disclosure.Button
                  ref={btnRef}
                  className="flex min-w-0 items-center gap-x-2 disabled:cursor-default"
                  disabled={singleActivity}
                  onClick={() => {
                    if (open) return
                    onClick?.(!open)
                  }}
                >
                  {singleActivity && <div className="shrink-0">{icon}</div>}
                  {title}
                  {(!hideChevronIcon || singleActivity) && <ChevronToggle open={open} className="text-center" />}
                </Disclosure.Button>
                <div className="flex shrink-0 items-center gap-x-2">
                  {actions && (
                    <div
                      onClick={() => {
                        if (open) return
                        btnRef.current?.click()
                      }}
                    >
                      {actions}
                    </div>
                  )}
                  <DateComponent
                    value={activityDate}
                    {...activityDateOptions}
                    className="text-caption-md text-minimal"
                  />
                </div>
              </div>
            )}
            {/*
              Aligned with the wells below rather than with the title, so the
              card has one left edge for everything that is not the heading.
              28px is the rail's own w-5 plus the row's gap-2.
            */}
            {!hideHeader && tags && (
              <div
                x-component-name="activity-record-tags"
                className={classNames(`flex flex-wrap gap-2`, !singleActivity && `-ml-7`)}
              >
                {tags}
              </div>
            )}
            {/*
              Reclaims the rail column, like the tags above. Only the title
              needs to sit beside the icon; a content box has nothing to line up
              with and was losing 28px of a 668px pane to make room for a line.
              Only while the rail exists: hiding the header removes the rail
              too, and reclaiming a column that is not there drags the record
              28px past the left padding of whatever surface is hosting it.
            */}
            <div x-component-name="activity-record-data" className={singleActivity || hideHeader ? `` : `-ml-7`}>
              <>
                {preview && !open && preview}
                {(drawer || aiContent || errorContent) && (
                  <HighlightWrapper active={highlightActive}>
                    <div
                      x-component-name="activity-content"
                      className={classNames(
                        `border-default`,
                        content ? `w-full items-center rounded-lg border bg-surface py-3 text-body-xs shadow-sm` : ``,
                        highlightActive && `highlight-pulse-inner border-blue-600`,
                      )}
                    >
                      {content && content}
                      <Disclosure.Panel>
                        {!errorContent && drawer}
                        {!errorContent && aiContent}
                        {errorContent}
                      </Disclosure.Panel>
                    </div>
                  </HighlightWrapper>
                )}
              </>
            </div>
          </>
        )}
      </Disclosure>
    </div>
  )
}

function HighlightWrapper({ active, children }: { active: boolean; children: React.ReactNode }) {
  if (!active) return <>{children}</>
  return <div className="highlight-pulse-wrapper rounded-lg">{children}</div>
}
